import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  buildLogisticsDashboardNavContext,
  getVisibleLogisticsDashboardModules,
  getVisibleLogisticsDashboardSubTabs,
  isSubTabActive,
  resolveActiveLogisticsDashboardModule,
} from "../config/logisticsDashboardModules";
import { buildLogisticsTabNotificationsPath } from "../config/logisticsDashboardNotifications";
import DashboardModuleTabBadge from "./DashboardModuleTabBadge";
import { useDashboardTabNotificationCounts } from "../hooks/useDashboardTabNotificationCounts";
import logisticsProviderDashboardService from "../services/logisticsProviderDashboard.service";

export function useLogisticsDashboardNav() {
  const [navEpoch, setNavEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setNavEpoch((n) => n + 1);
    window.addEventListener("storage", bump);
    return () => window.removeEventListener("storage", bump);
  }, []);

  const navCtx = useMemo(() => buildLogisticsDashboardNavContext(), [navEpoch]);
  const modules = useMemo(() => getVisibleLogisticsDashboardModules(navCtx), [navCtx]);
  const moduleKeys = useMemo(() => new Set(modules.map((m) => m.key)), [modules]);

  return { navCtx, modules, moduleKeys };
}

export default function LogisticsDashboardModuleNav({ showSubTabs = true, className = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { navCtx, modules } = useLogisticsDashboardNav();
  const fetchCounts = useCallback(
    () => logisticsProviderDashboardService.getTabNotificationCounts(),
    [],
  );
  const tabCounts = useDashboardTabNotificationCounts({
    enabled: navCtx.canAccessLogisticsDashboard,
    fetchCounts,
    pathname: location.pathname,
  });

  const activeModule = useMemo(
    () => resolveActiveLogisticsDashboardModule(location.pathname, modules),
    [location.pathname, modules],
  );
  const subTabs = useMemo(
    () =>
      showSubTabs && activeModule?.key !== "overview"
        ? getVisibleLogisticsDashboardSubTabs(activeModule.key)
        : [],
    [showSubTabs, activeModule],
  );

  if (!navCtx.canAccessLogisticsDashboard) return null;
  if (modules.length === 0) return null;

  return (
    <div className={`buyer-dashboard__module-nav ${className}`.trim()}>
      <div className="buyer-dashboard__module-tabs">
        {modules.map((tab) => {
          const isOverview = tab.key === "overview";
          const isActiveModule = activeModule?.key === tab.key;
          const onOverviewPage =
            location.pathname === "/dashboard/logistics" ||
            location.pathname === "/dashboard/logistics/";

          const notificationCount = Number(tabCounts[tab.key]) || 0;
          const hasNotifications = notificationCount > 0;

          let stateClass = "";
          if (isOverview && onOverviewPage) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          } else if (isActiveModule && !isOverview) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          } else if (isActiveModule && isOverview) {
            stateClass = " buyer-dashboard__module-tab--active-outline";
          }
          if (hasNotifications) {
            stateClass += " buyer-dashboard__module-tab--has-notifications";
          }

          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`buyer-dashboard__module-tab${stateClass}`}
            >
              <span className="buyer-dashboard__module-tab-label">{tab.label}</span>
              <DashboardModuleTabBadge
                count={notificationCount}
                tabKey={tab.key}
                onNavigate={navigate}
                buildPath={buildLogisticsTabNotificationsPath}
              />
            </Link>
          );
        })}
      </div>

      {subTabs.length > 0 && (
        <div className="buyer-dashboard__sub-tabs">
          {subTabs.map((tab) => {
            const active = isSubTabActive(location.pathname, tab);
            return (
              <Link
                key={tab.key}
                to={tab.to}
                className={`buyer-dashboard__sub-tab${active ? " buyer-dashboard__sub-tab--active" : ""}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
